import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  signal,
  ViewChild,
} from "@angular/core";
import { BehaviorSubject, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  ModalGalleryService,
  modeVisibleModal,
} from "../../modal-gallery/modal-gallery.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export class CommentSendStatus {
  public error: string;
  public loading: boolean;
}

const DRAFT_COMMENT_STORAGE_KEY = 'gallery_draft_comment';
const DRAFT_COMMENT_TIMESTAMP_KEY = 'gallery_draft_comment_timestamp';
const DRAFT_TTL_DAYS = 60;

@Component({
  selector: "ks-sender",

  templateUrl: "./sender.component.html",
  styleUrls: ["./sender.component.scss", "./sender.component-modern.scss"],
})
export class SenderComponent implements OnInit, OnDestroy, AfterViewInit {
  /**
   * Привязка к textarea.
   */
  @ViewChild("message") message: ElementRef;

  /**
   * Двухсторонний биндинг статуса формы.
   */
  @Input("formStatus") formStatus$: BehaviorSubject<CommentSendStatus>;
  @Input() imageId: number;
  @Input() isAllowCreateTask: boolean;
  @Output("formStatusChange") formStatusChange = new EventEmitter<
    BehaviorSubject<CommentSendStatus>
  >();

  /**
   * Событие отправки введенного сообщения.
   */
  @Output() onSendMessage = new EventEmitter<{
    comment: string;
    photos?: File[];
    isCreateTask: boolean;
  }>();
  isEmpty: boolean = false;
  placeholder: string = "Введите комментарий...";
  showPhotoReplacerModal: boolean = false;
  protected readonly photos = signal<File[]>([]);
  protected readonly isCheckedSetTaskMerch = signal<boolean>(false);

  /**
   * Subject для отписки.
   */
  private ngUnsubscribe = new Subject();

  constructor(private modalGalleryService: ModalGalleryService) {
    this.modalGalleryService
      .getPhotoToSend$()
      .pipe(takeUntilDestroyed())
      .subscribe((photos) => {
        this.photos.set(photos);
      });

    this.modalGalleryService
      .getCheckedSetTaskMerch$()
      .pipe(takeUntilDestroyed())
      .subscribe((status) => {
        this.isCheckedSetTaskMerch.set(status);
      });
  }

  ngOnInit() {
    // Задаем стартовые значения состояния формы.
    this.formStatus$ = new BehaviorSubject({ error: "", loading: false });
    this.formStatusChange.emit(this.formStatus$);

    // Подписываемся на состояние формы для отслеживания успешной отправки комментария и обнуления textarea
    this.formStatus$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((v) => {
      if (!this.message) return;

      if (v.error === "" && v.loading === false) {
        this.message.nativeElement.value = "";
        // Удаляем черновик после успешной отправки
        this.removeDraftComment();
      }
    });
  }
  
  ngAfterViewInit() {
    // Загружаем черновик после инициализации ViewChild
    this.loadDraftComment();
  }

  ngOnDestroy() {
    // Сохраняем черновик перед уничтожением компонента
    if (this.message?.nativeElement?.value) {
      this.saveDraftComment(this.message.nativeElement.value);
    }
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Сохранение черновика комментария в localStorage (один для всех фото)
   */
  private saveDraftComment(comment: string): void {
    try {
      if (comment && comment.trim()) {
        localStorage.setItem(DRAFT_COMMENT_STORAGE_KEY, comment);
        localStorage.setItem(DRAFT_COMMENT_TIMESTAMP_KEY, Date.now().toString());
      } else {
        localStorage.removeItem(DRAFT_COMMENT_STORAGE_KEY);
        localStorage.removeItem(DRAFT_COMMENT_TIMESTAMP_KEY);
      }
    } catch (e) {
      console.warn('Failed to save draft comment to localStorage:', e);
    }
  }
  
  /**
   * Загрузка черновика комментария из localStorage
   */
  private loadDraftComment(): void {
    if (!this.message) return;
    
    try {
      // Проверяем срок действия черновика
      if (this.isDraftExpired()) {
        this.removeDraftComment();
        return;
      }
      
      const draft = localStorage.getItem(DRAFT_COMMENT_STORAGE_KEY);
      if (draft) {
        this.message.nativeElement.value = draft;
      }
    } catch (e) {
      console.warn('Failed to load draft comment from localStorage:', e);
    }
  }
  
  /**
   * Проверка истечения срока действия черновика (60 дней)
   */
  private isDraftExpired(): boolean {
    try {
      const timestamp = localStorage.getItem(DRAFT_COMMENT_TIMESTAMP_KEY);
      if (!timestamp) return false;
      
      const savedTime = parseInt(timestamp, 10);
      const now = Date.now();
      const ttlMs = DRAFT_TTL_DAYS * 24 * 60 * 60 * 1000; // 60 дней в миллисекундах
      
      return (now - savedTime) > ttlMs;
    } catch (e) {
      return false;
    }
  }
  
  /**
   * Удаление черновика комментария из localStorage (после успешной отправки)
   */
  private removeDraftComment(): void {
    try {
      localStorage.removeItem(DRAFT_COMMENT_STORAGE_KEY);
      localStorage.removeItem(DRAFT_COMMENT_TIMESTAMP_KEY);
    } catch (e) {
      console.warn('Failed to remove draft comment from localStorage:', e);
    }
  }

  /**
   * Отправка сообщения
   * @param textarea
   */
  send(textarea: HTMLTextAreaElement) {
    if (!textarea.value) {
      //TODO Проверка на пустую форму и подсветка

      this.alertEmptyMessage();

      return;
    }
    this.onSendMessage.emit({
      comment: textarea.value,
      photos: this.photos(),
      isCreateTask: this.isCheckedSetTaskMerch(),
    });
    // Удаляем черновик после отправки
    this.removeDraftComment();
    this.modalGalleryService.onCheckedSetTaskMerch(false);
    this.modalGalleryService.clearPhotoToSend();
  }

  /**
   * Скрыть блок с ошибкой.
   */
  alertEmptyMessage() {
    this.isEmpty = true;
    this.placeholder = "Пустой комментарий";
  }

  // Скрыть блок с ошибкой.
  closeErrorBlock() {
    this.formStatus$.next({ error: "", loading: false });
  }

  resetAlert() {
    this.isEmpty = false;
    this.placeholder = "Введите комментарий...";
  }

  onPhotoChange(event, popup) {
    this.modalGalleryService.setPhotoToSend(event);
    this.closePhotoReplacerModal();
  }

  closePhotoReplacerModal() {
    // this.files = [];
    this.showPhotoReplacerModal = false;
  }
  onClipClick() {
    if (!this.isCheckedSetTaskMerch()) return;
    this.showPhotoReplacerModal = true;
  }

  onEditorOpen() {
    if (!this.isCheckedSetTaskMerch()) return;
    this.modalGalleryService.onOpenEditor();
  }

  protected onCheckedSetTaskMerch(event) {
    console.log("onCheckedSetTaskMerch", event);
    const checked = event.target.checked;
    this.modalGalleryService.onCheckedSetTaskMerch(checked);

    if (!checked) {
      this.modalGalleryService.setModeVisible(modeVisibleModal.MAIN);
    }
  }
}
